import React, { useEffect, useRef, useState } from 'react'
import { PEBarData } from '@components/chart/types'
import './ProgrammeExplorerGeoChart.css'

type Props = {
  countries: PEBarData[]
  regions: PEBarData[]
  onImageReady?: (image: string) => void
  onTop10ImageReady?: (image: string) => void
}

declare global {
  interface Window {
    google: any
  }
}

/** High-res canvas for PPT export (GeoChart maps to container pixel size). */
const EXPORT_MAP_WIDTH = 960
const EXPORT_MAP_HEIGHT = 420

const ProgrammeExplorerGeoChart: React.FC<Props> = ({ countries, regions, onImageReady, onTop10ImageReady }) => {
  const countriesRef = useRef<HTMLDivElement | null>(null)
  const regionsRef = useRef<HTMLDivElement | null>(null)
  const regionsExportRef = useRef<HTMLDivElement | null>(null)
  const top10Ref = useRef<HTMLDivElement | null>(null)
  const [loaded, setLoaded] = useState<boolean>(false)
  const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  const nameToCountryCode = (raw: string): string | null => {
    const key = (raw || '').toLowerCase().trim()
    const map: Record<string, string> = {
        'afghanistan': 'af',
        'albania': 'al',  
        'algeria': 'dz',
        'andorra': 'ad',
        'angola': 'ao',
        'argentina': 'ar',
        'armenia': 'am',
        'australia': 'au',
        'austria': 'at',
        'azerbaijan': 'az',
        'bahrain': 'bh',
        'bangladesh': 'bd',
        'belarus': 'by',
        'belgium': 'be',
        'belize': 'bz',
        'benin': 'bj',
        'bhutan': 'bt',
        'bolivia': 'bo',
        'bosnia and herzegovina' : 'ba',
        'botswana': 'bw', 
        'brazil': 'br',
        'brunei': 'bn',
        'bulgaria': 'bg',
        'burkina faso': 'bf',
        'burundi': 'bi',
        'cambodia': 'kh',
        'cameroon': 'cm', 
        'canada': 'ca',
        'cape verde': 'cv',
        'central african republic': 'cf',
        'chad': 'td',
        'chile': 'cl',
        'china': 'cn',
        'colombia': 'co',
        'comoros': 'km',
        'democratic republic of the congo': 'cd',
        'republic of the congo': 'cg',
        'costa rica': 'cr',
        'croatia': 'hr',
        'cuba': 'cu',
        'cyprus': 'cy',
        'czechia': 'cz',
        'denmark': 'dk',
        'djibouti': 'dj',
        'dominican republic': 'do',
        'ecuador': 'ec',
        'egypt': 'eg',
        'el salvador': 'sv',
        'equatorial guinea': 'gq',
        'eritrea': 'er',
        'estonia': 'ee',
        'eswatini': 'sz',
        'ethiopia': 'et',
        'fiji': 'fj',
        'finland': 'fi',
        'france': 'fr',
        'gabon': 'ga',
        'gambia': 'gm',
        'georgia': 'ge',
        'germany': 'de',
        'ghana': 'gh',
        'greece': 'gr',
        'guatemala': 'gt',
        'guinea': 'gn',
        'guinea-bissau': 'gw',
        'haiti': 'ht',
        'honduras': 'hn',
        'hungary': 'hu',
        'iceland': 'is',
        'india': 'in',
        'indonesia': 'id',
        'iran': 'ir',
        'iraq': 'iq',
        'ireland': 'ie',
        'israel': 'il',
        'italy': 'it',
        'jamaica': 'jm',
        'japan': 'jp',
        'jordan': 'jo',
        'kazakhstan': 'kz',
        'kenya': 'ke',
        'kuwait': 'kw',
        'kyrgyzstan': 'kg',
        'laos': 'la',
        'latvia': 'lv',
        'lebanon': 'lb',
        'lesotho': 'ls',
        'liberia': 'lr',
        'libya': 'ly',
        'liechtenstein': 'li',
        'lithuania': 'lt',
        'luxembourg': 'lu',
        'madagascar': 'mg',
        'malawi': 'mw',
        'malaysia': 'my',
        'maldives': 'mv',
        'mali': 'ml',
        'malta': 'mt',
        'mauritania': 'mr',
        'mauritius': 'mu',
        'mexico': 'mx',
        'moldova': 'md',
        'monaco': 'mc',
        'mongolia': 'mn',
        'montenegro': 'me',
        'morocco': 'ma',
        'mozambique': 'mz',
        'myanmar': 'mm',
        'namibia': 'na',
        'nepal': 'np',
        'netherlands': 'nl',
        'new zealand': 'nz',
        'nicaragua': 'ni',
        'niger': 'ne',
        'nigeria': 'ng',
        'north macedonia': 'mk',
        'norway': 'no',
        'oman': 'om',
        'pakistan': 'pk',
        'palau': 'pw',
        'palestine': 'ps',
        'panama': 'pa',
        'papua new guinea': 'pg',
        'paraguay': 'py',
        'peru': 'pe',
        'philippines': 'ph',
        'poland': 'pl',
        'portugal': 'pt',
        'qatar': 'qa',
        'romania': 'ro',
        'russia': 'ru',
        'rwanda': 'rw',
        'saudi arabia': 'sa',
        'senegal': 'sn',
        'serbia': 'rs',
        'seychelles': 'sc',
        'sierra leone': 'sl',
        'singapore': 'sg',
        'slovakia': 'sk',
        'slovenia': 'si',
        'somalia': 'so',
        'south africa': 'za',
        'south korea': 'kr',
        'south sudan': 'ss',  
        'spain': 'es',
        'sri lanka': 'lk',
        'sudan': 'sd',
        'sweden': 'se',
        'switzerland': 'ch',
        'syria': 'sy',
        'taiwan': 'tw',
        'tajikistan': 'tj',
        'tanzania': 'tz',
        'thailand': 'th',
        'timor-leste': 'tl',
        'togo': 'tg',
        'tonga': 'to',
        'tunisia': 'tn',
        'turkey': 'tr',
        'turkmenistan': 'tm',
        'uganda': 'ug',
        'ukraine': 'ua',
        'united arab emirates': 'ae',
        'united kingdom': 'gb',
        'united states': 'us',
        'uruguay': 'uy',
        'uzbekistan': 'uz',
        'vatican city': 'va',
        'venezuela': 've',
        'vietnam': 'vn',
        'yemen': 'ye',
        'zambia': 'zm',
        'zimbabwe': 'zw',
        'european union': 'eu',
        'united nations': 'un',
        'puerto rico': 'pr',
    }

    if (map[key]) return map[key]
    return null
  }

  const inlineComputedStyles = (node: HTMLElement) => {
    const walk = (el: Element) => {
      const style = window.getComputedStyle(el as Element)
      const css: Record<string, string> = {}
      for (let i = 0; i < style.length; i++) {
        const prop = style[i]
        css[prop] = style.getPropertyValue(prop)
      }
      ;(el as HTMLElement).setAttribute('style', Object.entries(css).map(([k, v]) => `${k}:${v}`).join(';'))
      Array.from(el.children).forEach(child => walk(child))
    }
    walk(node)
  }

  const elementToPngDataURI = (container: HTMLElement | null): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!container) return resolve(null)
      const clone = container.cloneNode(true) as HTMLElement
      inlineComputedStyles(clone)
      const rect = container.getBoundingClientRect()
      const width = Math.max(Math.round(rect.width), 10)
      const height = Math.max(Math.round(rect.height), 10)
      const scale = 2
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      svg.setAttribute('width', `${width * scale}`)
      svg.setAttribute('height', `${height * scale}`)
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`)
      const fo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')
      fo.setAttribute('x', '0')
      fo.setAttribute('y', '0')
      fo.setAttribute('width', '100%')
      fo.setAttribute('height', '100%')
      const wrapper = document.createElement('div')
      wrapper.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml')
      wrapper.appendChild(clone)
      fo.appendChild(wrapper as any)
      svg.appendChild(fo)
      const xml = new XMLSerializer().serializeToString(svg)
      const svg64 = window.btoa(unescape(encodeURIComponent(xml)))
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = width * scale
        canvas.height = height * scale
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          const uri = canvas.toDataURL('image/png')
          console.log('[Export][GeoChart] foreignObject PNG ready. Length:', uri.length, 'Preview:', uri.slice(0, 40))
          resolve(uri)
        } else {
          resolve(null)
        }
      }
      img.onerror = () => resolve(null)
      img.src = 'data:image/svg+xml;base64,' + svg64
    });
  }

  const loadGoogle = () => {
    if (window.google && window.google.charts) {
      setLoaded(true)
      return
    }
    const scriptId = 'google-charts-loader'
    const existing = document.getElementById(scriptId)
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true))
      return
    }
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'text/javascript'
    script.src = 'https://www.gstatic.com/charts/loader.js'
    script.onload = () => setLoaded(true)
    document.head.appendChild(script)
  }

  const drawCharts = () => {
    if (!window.google || !window.google.charts || !loaded) return
    const loadOpts: any = { packages: ['geochart'] }
    if (MAPS_API_KEY) {
      loadOpts.mapsApiKey = MAPS_API_KEY
    } else {
      console.warn('[Export][GeoChart] REACT_APP_GOOGLE_MAPS_API_KEY is not set. GeoChart may fail to render/export.')
    }
    window.google.charts.load('current', loadOpts);
    const emitGeoMapExport = (chart: { getImageURI?: () => string }, container: HTMLElement | null) => {
      try {
        const uri = chart.getImageURI && chart.getImageURI()
        if (uri && typeof uri === 'string' && uri.startsWith('data:image/png') && onImageReady) {
          onImageReady(uri)
          return
        }
      } catch (e) {
        // fall through to DOM snapshot
      }
      if (!container || !onImageReady) return
      elementToPngDataURI(container).then((uri) => {
        if (uri && typeof uri === 'string' && uri.startsWith('data:image/png')) {
          onImageReady(uri)
        }
      })
    }

    const drawRegionsGeoChart = (container: HTMLElement | null, forExport: boolean) => {
      if (!container || regions.length === 0) return

        const centers: Record<string, { lat: number; lon: number; label: string }> = {
          europe: { lat: 54, lon: 15, label: 'Europe' },
          asia: { lat: 35, lon: 100, label: 'Asia' },
          africa: { lat: 0, lon: 20, label: 'Africa' },
          oceania: { lat: -25, lon: 135, label: 'Oceania' },
          north_america: { lat: 45, lon: -100, label: 'North America' },
          south_america: { lat: -15, lon: -60, label: 'South America' },
          americas: { lat: 10, lon: -75, label: 'Americas' },
          middle_east: { lat: 29, lon: 41, label: 'Middle East' },
          emea: { lat: 30, lon: 20, label: 'EMEA' },
          apac: { lat: 10, lon: 120, label: 'APAC' },
          mea: { lat: 15, lon: 30, label: 'MEA' },
        };
        const resolveCenter = (raw: string): { lat: number; lon: number; label: string } | null => {
          const key = (raw || '').toLowerCase().trim();
          if (key === 'eu') return centers.europe;
          if (key === 'apac') return centers.apac;
          if (key === 'latam') return centers.south_america;
          if (key === 'na') return centers.north_america;
          if (key === 'mea') return centers.mea;
          if (key.includes('emea')) return centers.emea;
          if (key.includes('apac') || key.includes('asia pacific') || key.includes('asia-pacific') || key.includes('asia pacífico') || key.includes('asia pacifico')) return centers.apac;
          if (key.includes('middle east') || key.includes('oriente medio') || key.includes('mena') || key.includes('gcc') || key.includes('gulf')) return centers.middle_east;
          if (key.includes('north america') || key.includes('norteamérica') || key.includes('norte america') || (key.includes('america') && key.includes('north'))) return centers.north_america;
          if (key.includes('south america') || key.includes('latam') || key.includes('latin america') || key.includes('sudamérica') || key.includes('sudamerica')) return centers.south_america;
          if (key.includes('americas') || key === 'america' || key === 'américa') return centers.americas;
          if (key.includes('oceania') || key.includes('australia') || key.includes('new zealand') || key.includes('anz')) return centers.oceania;
          if (key.includes('africa') || key.includes('áfrica') || key.includes('sub-saharan') || key.includes('subsaharan')) return centers.africa;
          if (key.includes('europe') || key.includes('europa')) return centers.europe;
          if (key.includes('asia')) return centers.asia;
          return null;
        };
        const rows = regions
          .map((r) => {
            const center = resolveCenter(r.name || '');
            if (!center) return null;
            return [center.lat, center.lon, center.label, r.data] as (string | number)[];
          })
          .filter((e) => Array.isArray(e)) as (string | number)[][];
        if (rows.length > 0) {
          const dataRegions: (string | number)[][] = [
            ['Lat', 'Lon', 'Region', '%'] as (string | number)[],
          ].concat(rows);
          const dataTable = window.google.visualization.arrayToDataTable(dataRegions);
          const chartRegions = new window.google.visualization.GeoChart(container);
          const opts = {
            region: 'world',
            displayMode: 'markers',
            legend: 'none',
            sizeAxis: { minValue: 0, maxValue: 100 },
            colorAxis: { colors: ['#fee8c8', '#e34a33'] },
            tooltip: { isHtml: true },
            backgroundColor: '#ffffff',
          } as any;
          window.google.visualization.events.addListener(chartRegions, 'ready', () => {
            if (!forExport) return;
            emitGeoMapExport(chartRegions, container);
          });
          chartRegions.draw(dataTable, opts);
        }
    };

    const onChartsLoaded = () => {
      drawRegionsGeoChart(regionsRef.current, false);
      drawRegionsGeoChart(regionsExportRef.current, true);
    };
    window.google.charts.setOnLoadCallback(onChartsLoaded);
  }

  useEffect(() => {
    loadGoogle()
  }, [])

  useEffect(() => {
    drawCharts()
  }, [loaded, countries, regions])

  useEffect(() => {
    // Disabled frontend snapshotting of top10_countries as it is rendered natively in Backend
  }, [countries, onTop10ImageReady])

  return (
    <div className='d-flex flex-column gap-10'>
      <div>
        <div className='fs-5 mb-3'>Top 10 Countries (% of visits)</div>
        <div ref={top10Ref} style={{ position: 'relative', height: 120, padding: '0 40px', background: '#fff', borderRadius: 8 }}>
          <div style={{ position: 'absolute', top: 58, left: 40, right: 40, height: 2, background: '#c9d6dd' }} />
          <div style={{ position: 'absolute', top: 0, bottom: 0, left: 40, right: 40 }}>
            {countries.length > 0 ? (() => {
              const items = [...countries].sort((a, b) => (b.data as number) - (a.data as number)).slice(0, 10)
              const n = items.length
              return items.map((c, i) => {
                const leftPct = n > 1 ? (i * 100) / (n - 1) : 0
                const code = nameToCountryCode(c.name)
                const flagUrl = code ? `/media/svg/flags/${code.toLowerCase()}.svg` : ''
                const circleSize = 26
                const shortenName = (name: string) => {
                  const m: Record<string, string> = {
                    'united states': 'USA',
                    'united kingdom': 'UK'
                  }
                  return m[name.toLowerCase()] || name;
                }
                const shortName = shortenName(c.name)

                return (
                  <div
                    key={c.name}
                    style={{
                      position: 'absolute',
                      left: `${leftPct}%`,
                      top: 45,
                      width: circleSize,
                      height: circleSize,
                      transform: 'translateX(-50%)',
                      borderRadius: '50%',
                      background: '#5d8399',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}
                  >
                    {flagUrl ? (
                      <img src={flagUrl} alt={c.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                    ) : (
                      <span style={{ fontSize: 14 }}>{c.name.charAt(0)}</span>
                    )}
                    <div style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', width: 70, textAlign: 'center', fontSize: 11, color: '#5d6b75', whiteSpace: 'nowrap', overflow: 'visible' }}>
                      {shortName}
                    </div>
                    <div style={{ position: 'absolute', top: circleSize + 4, left: '50%', transform: 'translateX(-50%)', width: 60, textAlign: 'center', fontSize: 12, color: '#5d6b75' }}>
                      {c.data}%
                    </div>
                    <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: circleSize + 22, width: 6, height: 6, borderRadius: '50%', background: '#a0b4be' }} />
                  </div>
                )
              })
            })() : <span className='text-muted'>No data available</span>}
          </div>
        </div>
      </div>
      <div>
        <div className='fs-5 mb-3'>Visits by region (%)</div>
        <div
          ref={regionsExportRef}
          aria-hidden='true'
          style={{
            position: 'fixed',
            left: -10000,
            top: 0,
            width: EXPORT_MAP_WIDTH,
            height: EXPORT_MAP_HEIGHT,
            opacity: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        />
        <div ref={regionsRef} style={{ width: '100%', height: 400 }} />
        <div className='mt-4'>
          {regions.length > 0 && (
            <div className='row'>
              {regions.map((r) => (
                <div key={r.name} className='col-md-6 mb-2'>
                  <span className='fw-bold'>{r.name}</span>: <span>{r.data}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProgrammeExplorerGeoChart
